'use client';

import { Fragment, useState } from 'react';
import {
  Input,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels
} from '@headlessui/react';
import { type ProductWithRelated } from '@/sanity/schemaTypes/documents/product';
import { formatPrice } from '@/lib/utils';
import SanityImage from '@/components/media/SanityImage';
import { twMerge } from 'tailwind-merge';
import Breadcrumb from '@/components/Breadcrumb';
import { type ProductPageTranslations } from '@/sanity/schemaTypes/objects/translations/productPageTranslationsType';
import PortableText from '@/components/portableText/PortableText';
import { useActionState } from 'react';
import { addItem } from '@/components/cart/actions';
import { useCart } from '@/components/cart/cart-context';
import { type ColorPicker } from '@/sanity/schemaTypes/singletons/colorPickerType';
import ProductCard from '@/components/ProductCard';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';

export default function ProductPage({
  data,
  locale
}: {
  data: {
    product: ProductWithRelated;
    productPageTranslations: ProductPageTranslations;
    colors: ColorPicker;
  };
  locale: string;
}) {
  const { product, productPageTranslations: translations, colors } = data;
  const [selectedColor, setSelectedColor] = useState(colors.colorCategories[0]?.colors[0]);
  const [selectedSize, setSelectedSize] = useState(product.variants[0]);
  const selecteSize = (size: string) => {
    const selected = product.variants.find((variant) => variant.title === size);
    if (selected) {
      setSelectedSize(selected);
    }
  };
  const [orientationInverted, setOrientationInverted] = useState(false);
  const [personalizations, setPersonalizations] = useState<
    { title: string; type: 'text' | 'number' | 'color'; value: string | number }[]
  >(
    product.personalizations.map((p) => ({
      title: p.title,
      type: p.type,
      value: ''
    }))
  );

  const { addCartItem } = useCart();
  const [, formAction, addItemPending] = useActionState(addItem, null);

  return (
    <div className="bg-white">
      <div className="pt-6 pb-16 sm:pb-24">
        <Breadcrumb collection={product.mainCollection} currentPage={product.title} />
        <div className="mx-auto mt-8 max-w-2xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <div className="lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-x-8">
            <div className="flex items-start justify-between gap-3 lg:col-span-5 lg:col-start-8">
              <h1 className="text-xl font-medium text-gray-900">{product.title}</h1>
              <p className="text-secondary bg-primary -mt-1 rounded-full px-4 py-1 text-xl font-medium shadow-md">
                {selectedSize?.price && formatPrice(selectedSize.price, locale)}
              </p>
            </div>

            {/* Image gallery */}
            <div className="mt-8 lg:col-span-7 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:mt-0">
              <h2 className="sr-only">{translations.imagesGalleryTitle}</h2>

              <div className="grid grid-cols-2 gap-8">
                {/* Mask */}
                <a
                  href={`#carousel-0`}
                  style={{
                    backgroundColor: selectedColor?.colorCode
                  }}
                  className="col-span-2 row-span-2 w-full cursor-pointer"
                >
                  <SanityImage
                    className={`ratio-[800/600] h-auto w-full ${selectedColor?.colorCode === '#fcfcfb' ? 'opacity-10 invert' : ''} ${orientationInverted ? '-scale-x-100' : ''}`}
                    priority={true}
                    width={400}
                    height={300}
                    // image is 650px on screen smaller than lg (< 64rem), and 700px on bigger screens
                    sizes={'(max-width: 64rem) 650px, 700px'}
                    alt={product.title}
                    src={product.mask.asset?._ref}
                  />
                </a>
                {/* Images */}
                {product.images.map((image, i) => (
                  <a
                    key={i}
                    href={`#carousel-${i + 1}`}
                    className="cursor-pointer overflow-hidden rounded-lg"
                  >
                    <SanityImage
                      className={`ratio-[800/600] h-auto w-full ${orientationInverted ? '-scale-x-100' : ''}`}
                      width={300}
                      height={225}
                      // images are half the mask (mask: 650px on screen smaller than lg (< 64rem), and 700px on bigger screens)
                      sizes="(max-width: 64rem) 325px, 350px"
                      alt={product.title}
                      src={image.asset?._ref}
                    />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-8 lg:col-span-5">
              <form
                action={async () => {
                  const attributes = [];
                  if (selectedColor) {
                    attributes.push({
                      key: translations.colorLabelTranslation,
                      value: selectedColor.title
                    });
                  }
                  if (orientationInverted) {
                    attributes.push({
                      key: translations.orientationLabelTranslation,
                      value: translations.invertedTranslation
                    });
                  }
                  personalizations.forEach((personalization) => {
                    if (!personalization.value) return;
                    attributes.push({
                      key: personalization.title,
                      value: `${personalization.value}`
                    });
                  });
                  addCartItem(
                    (product.shopifyProduct.variants as any).find(
                      (v: any) => v.title === selectedSize?.title
                    ),
                    product.shopifyProduct as any,
                    attributes
                  );
                  await formAction({
                    selectedVariantId: (product.shopifyProduct.variants as any).find(
                      (v: any) => v.title === selectedSize?.title
                    ).gid,
                    attributes
                  });
                }}
              >
                {/* Color picker */}
                <div>
                  <h2 className="text-sm font-medium text-gray-900">
                    {translations.colorLabelTranslation}
                    {selectedColor && (
                      <>
                        <span className="px-1">:</span>
                        <span className="font-medium">{selectedColor?.title}</span>
                      </>
                    )}
                  </h2>

                  <fieldset aria-label={translations.colorLabelTranslation} className="mt-2">
                    <RadioGroup
                      value={selectedColor}
                      onChange={setSelectedColor}
                      className="flex flex-wrap items-center gap-3"
                    >
                      {colors.colorCategories?.map((colorCategory, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-3">
                          {colorCategory.colors?.map((color, j) => (
                            <Radio
                              key={j}
                              value={color}
                              aria-label={color.title}
                              style={{ backgroundColor: color.colorCode }}
                              aria-checked={selectedColor === color}
                              className={
                                'ring-secondary relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full border-[1px] border-gray-300 p-0.5 focus:outline-hidden data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3'
                              }
                            >
                              <span
                                aria-hidden="true"
                                style={{ borderColor: color.colorCode }}
                                className={'size-8 rounded-full border border-black/10'}
                              />
                              <span className="sr-only">{color.title}</span>
                            </Radio>
                          ))}
                        </div>
                      ))}
                    </RadioGroup>
                  </fieldset>
                </div>

                {/* Size picker */}
                <div className="mt-8">
                  <h2 className="text-sm font-medium text-gray-900">
                    {product.tailleLabel ? product.tailleLabel : translations.sizeLabelTranslation}
                  </h2>
                  <fieldset
                    aria-label={
                      product.tailleLabel ? product.tailleLabel : translations.sizeLabelTranslation
                    }
                    className="mt-2"
                  >
                    <RadioGroup
                      value={selectedSize?.title}
                      onChange={selecteSize}
                      className="grid grid-cols-3 gap-3"
                    >
                      {product.variants.map((variant, i) => (
                        <Radio
                          key={i}
                          value={variant.title}
                          className="data-checked:bg-secondary data-checked:hover:bg-secondary/90 data-focus:ring-secondary/80 flex cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-3 text-sm font-medium text-nowrap text-gray-900 hover:bg-gray-50 focus:outline-hidden data-checked:border-transparent data-checked:text-white data-focus:ring-2 data-focus:ring-offset-2 sm:flex-1"
                        >
                          {variant.title}
                        </Radio>
                      ))}
                    </RadioGroup>
                  </fieldset>
                </div>

                {/* Orientation picker */}
                <div className="mt-8 flex items-center gap-6">
                  <h2 className="text-sm font-medium text-gray-900">
                    {translations.invertLabelTranslation}
                  </h2>
                  <fieldset aria-label={translations.invertLabelTranslation}>
                    <Switch
                      checked={orientationInverted}
                      onChange={setOrientationInverted}
                      className="group data-[checked]:bg-secondary bg-secondary/20 data-focus:ring-secondary/80 inline-flex h-6 w-11 items-center rounded-full transition focus:outline-hidden data-focus:ring-2 data-focus:ring-offset-2"
                    >
                      <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
                    </Switch>
                  </fieldset>
                </div>

                {/* Personnalizations */}
                {personalizations.map((personalization, i) => (
                  <div className="mt-8" key={i}>
                    <h2 className="text-sm font-medium text-gray-900">{personalization.title}</h2>
                    <Input
                      type={personalization.type}
                      value={personalization.value}
                      onChange={(e) => {
                        const newPersonalizations = [...personalizations];
                        if (newPersonalizations[i]) {
                          newPersonalizations[i].value = e.target.value;
                        }
                        setPersonalizations(newPersonalizations);
                      }}
                      className="data-focus:ring-secondary/80 mt-2 rounded-md border border-gray-200 bg-white px-3 py-3 text-sm font-medium text-nowrap text-gray-900 hover:bg-gray-50 focus:outline-hidden data-focus:ring-2 data-focus:ring-offset-2"
                    />
                  </div>
                ))}

                {/* Add to cart button */}
                <div className="sticky bottom-0 mt-8 bg-gradient-to-t from-white to-transparent pt-2 pb-6">
                  <button
                    type="submit"
                    disabled={addItemPending}
                    className="bg-secondary hover:text-secondary hover:bg-primary focus:ring-primary flex w-full cursor-pointer items-center justify-center rounded-md border border-transparent px-8 py-3 text-base font-medium text-white duration-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
                  >
                    <span className="font-semibold">
                      {addItemPending
                        ? translations.loadingTranslation
                        : translations.addToCartLabelTranslation}
                    </span>
                    {selectedSize?.price && (
                      <span className="ml-1 opacity-80">
                        ({formatPrice(selectedSize?.price, locale)})
                      </span>
                    )}
                  </button>
                </div>
              </form>

              {/* Product details */}
              <div className="mt-10">
                <h2 className="text-sm font-medium text-gray-900">
                  {translations.descriptionLabelTranslation}
                </h2>
                <div className="prose">
                  <PortableText
                    blocks={product.description}
                    className="mt-4 space-y-4 text-sm/6 text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Tabs */}
        <section
          aria-labelledby="features-heading"
          className="mx-auto max-w-7xl py-12 sm:px-2 lg:px-8"
        >
          <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
            <TabGroup className="mt-4">
              <div className="-mx-4 flex overflow-x-auto sm:mx-0">
                <div className="flex-auto border-b border-gray-200 px-4 sm:px-0">
                  <TabList className="mx-auto -mb-px flex max-w-prose space-x-10">
                    <Tab className="data-selected:border-secondary/90 data-selected:text-secondary border-b-2 border-transparent py-6 text-sm font-medium whitespace-nowrap text-gray-500 hover:border-gray-300 hover:text-gray-700">
                      {translations.moreInfoLabelTranslation}
                    </Tab>
                    <Tab className="data-selected:border-secondary/90 data-selected:text-secondary border-b-2 border-transparent py-6 text-sm font-medium whitespace-nowrap text-gray-500 hover:border-gray-300 hover:text-gray-700">
                      {translations.installationInstructionsLabelTranslation}
                    </Tab>
                    <Tab className="data-selected:border-secondary/90 data-selected:text-secondary border-b-2 border-transparent py-6 text-sm font-medium whitespace-nowrap text-gray-500 hover:border-gray-300 hover:text-gray-700">
                      {translations.technicalSpecificationsLabelTranslation}
                    </Tab>
                  </TabList>
                </div>
              </div>

              <TabPanels as={Fragment}>
                <TabPanel className="prose mx-auto space-y-16 pt-10 lg:pt-16">
                  <PortableText blocks={product.body} />
                </TabPanel>
                <TabPanel className="prose mx-auto space-y-16 pt-10 lg:pt-16">
                  <PortableText blocks={translations.installationInstructions} />
                </TabPanel>
                <TabPanel className="prose mx-auto space-y-16 pt-10 lg:pt-16">
                  <PortableText blocks={translations.technicalSpecifications} />
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </div>
        </section>

        {/* Related products */}
        <section
          aria-labelledby="related-heading"
          className="mx-auto max-w-7xl py-12 sm:px-2 lg:px-8"
        >
          <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
            <h2 id="related-heading" className="mx-auto max-w-prose text-lg text-gray-900">
              {translations.relatedProductsLabelTranslation}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4 xl:gap-8">
              {product.relatedProducts.map((relatedProduct, i) => (
                <ProductCard
                  product={relatedProduct}
                  key={i}
                  className="group relative"
                  locale={locale}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Images full css carousel */}
      {[product.mask, ...product.images].map((image, i) => (
        <div
          aria-hidden="true"
          key={i}
          className="fixed inset-0 isolate -z-50 grid size-full place-content-center bg-gray-800/30 opacity-0 backdrop-blur-xs duration-300 target:z-50 target:opacity-100"
          id={`carousel-${i}`}
        >
          <a href="#" className="fixed inset-0 z-10 cursor-pointer">
            <span className="sr-only">close</span>
          </a>
          <div
            style={{
              backgroundColor:
                i === 0 && selectedColor?.colorCode === '#fcfcfb' ? 'rgb(227,227,225)' : 'white'
            }}
            className={`${i === 0 ? 'p-5' : ''} relative isolate z-20 m-5 rounded-lg shadow-lg`}
          >
            <a
              href="#"
              className="bg-secondary absolute top-2 right-2 z-20 cursor-pointer rounded p-2 shadow-md duration-150 hover:scale-105"
            >
              <XMarkIcon className="text-primary h-6 w-6" />
            </a>
            <div className="absolute left-0 z-10 grid h-full place-content-center lg:-translate-x-16">
              <a
                href={`#carousel-${i === 0 ? product.images.length : i - 1}`}
                className="bg-secondary m-2 block cursor-pointer rounded p-2 shadow-md duration-150 hover:scale-105"
              >
                <ChevronLeftIcon className="text-primary h-6 w-6" />
              </a>
            </div>
            <div className="absolute right-0 z-10 grid h-full place-content-center lg:translate-x-16">
              <a
                href={`#carousel-${i === product.images.length ? 0 : i + 1}`}
                className="bg-secondary m-2 block cursor-pointer rounded p-2 shadow-md duration-150 hover:scale-105"
              >
                <ChevronRightIcon className="text-primary h-6 w-6" />
              </a>
            </div>
            <div
              style={{ backgroundColor: i == 0 ? selectedColor?.colorCode : 'transparent' }}
              className={i !== 0 ? 'overflow-hidden rounded-lg' : ''}
            >
              <SanityImage
                className={twMerge(
                  `h-auto w-[800px] max-w-full ${orientationInverted ? '-scale-x-100' : ''}`,
                  i === 0 && selectedColor?.colorCode === '#fcfcfb' ? 'opacity-10 invert' : '',
                  orientationInverted ? '-scale-x-100' : ''
                )}
                width={800}
                height={600}
                sizes="(max-width: 800px) 100vw, 800px"
                alt={product.title}
                src={image.asset?._ref}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
